import React, { useState } from 'react';
import axios from 'axios';
import { biosdkOperationUrl, CONVERTER_SINGLE_API_BASE, logBiosdkUiRoute } from './config';
import { Eye, Upload, Download, Loader2, AlertCircle, CheckCircle2, Clock } from 'lucide-react';
import { Button } from './components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import { Label } from './components/ui/label';
import { Select } from './components/ui/select';
import { Badge } from './components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from './components/ui/alert';

function IrisExtractor() {
  const [file, setFile] = useState(null);
  const [irisType, setIrisType] = useState('Left');
  const [templateBlobUrl, setTemplateBlobUrl] = useState(null);
  const [injpegImageUrl, setInJpegImageUrl] = useState(null);
  const [jp2isoImageUrl, setJp2IsoImageUrl] = useState(null);
  const [timing, setTiming] = useState({
    convertInput: 0,
    convertJpeg: 0,
    extractionDurationMilliSeconds: 0,
  });
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [extractResult, setExtractResult] = useState(null);

  const arrayBufferToBase64 = (buffer) => {
    let binary = '';
    const bytes = new Uint8Array(buffer);
    for (let i = 0; i < bytes.length; i += 1) {
      binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
  };

  const toSignedByteJson = (arrayBuffer) => {
    const unsigned = new Uint8Array(arrayBuffer);
    const signed = Array.from(unsigned, (v) => (v > 127 ? v - 256 : v));
    return JSON.stringify(signed);
  };

  const handleFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    setFile(selectedFile);
    setExtractResult(null);
    setError(null);
    setSubmitted(false);
    setShowAdvanced(false);
    setTemplateBlobUrl(null);
    setJp2IsoImageUrl(null);
    setInJpegImageUrl(null);

    if (!selectedFile) {
      setError('No file selected.');
      return;
    }

    const fileExtension = selectedFile.name.split('.').pop().toLowerCase();
    let startConvertInput = 0;
    let convertInputDuration = 0;

    try {
      if (['jpeg', 'jpg', 'bmp', 'png'].includes(fileExtension)) {
        setInJpegImageUrl(URL.createObjectURL(selectedFile));
      } else if (fileExtension === 'iso') {
        const isoFormData = new FormData();
        isoFormData.append('file', selectedFile);
        startConvertInput = performance.now();

        const response = await axios.post(`${CONVERTER_SINGLE_API_BASE}/convertFromIsoFile?modality=IRIS`, isoFormData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          responseType: 'arraybuffer',
        });

        const endConvertInput = performance.now();
        convertInputDuration = endConvertInput - startConvertInput;
        const jpegBlob = new Blob([response.data], { type: 'image/jpeg' });
        setInJpegImageUrl(URL.createObjectURL(jpegBlob));
      } else if (fileExtension === 'jp2') {
        const formData = new FormData();
        formData.append('file', selectedFile);
        const isoResponse = await axios.post(`${CONVERTER_SINGLE_API_BASE}/convertImageFileToIso?modality=IRIS`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          responseType: 'arraybuffer',
        });

        const isoBlob = new Blob([isoResponse.data], { type: 'application/octet-stream' });
        const isoUrl = URL.createObjectURL(isoBlob);
        setJp2IsoImageUrl(isoUrl);

        const isoFormData = new FormData();
        isoFormData.append('file', isoBlob, 'converted-jp2.iso');

        const jpegResponse = await axios.post(`${CONVERTER_SINGLE_API_BASE}/convertFromIsoFile?modality=IRIS`, isoFormData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          responseType: 'arraybuffer',
        });

        const jpegBlob = new Blob([jpegResponse.data], { type: 'image/jpeg' });
        setInJpegImageUrl(URL.createObjectURL(jpegBlob));
      } else {
        setError('Unsupported file type. Please upload JPEG, PNG, BMP, JP2, or ISO files.');
        setFile(null);
        return;
      }

      setTiming((prevTiming) => ({
        ...prevTiming,
        convertInput: convertInputDuration,
      }));
    } catch (err) {
      setError('An error occurred while processing the file.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitted(true);
    setIsProcessing(true);
    setError(null);
    setExtractResult(null);
    setTemplateBlobUrl(null);

    if (!file) {
      setError('Please upload an iris image file.');
      setIsProcessing(false);
      return;
    }

    const fileExtension = file.name.split('.').pop().toLowerCase();
    let irisbdbPlaceholder = '';
    let convertJpegDuration = 0;

    try {
      const startConvert = performance.now();
      if (['jpeg', 'jpg', 'bmp', 'png'].includes(fileExtension)) {
        const formData = new FormData();
        formData.append('file', file);
        const isoResponse = await axios.post(`${CONVERTER_SINGLE_API_BASE}/convertImageFileToIso?modality=IRIS`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          responseType: 'arraybuffer',
        });
        convertJpegDuration = performance.now() - startConvert;
        irisbdbPlaceholder = toSignedByteJson(isoResponse.data);
      } else if (fileExtension === 'jp2') {
        if (!jp2isoImageUrl) throw new Error('JP2 preview not ready; re-select the file.');
        const isoBuf = await fetch(jp2isoImageUrl).then((res) => res.arrayBuffer());
        convertJpegDuration = performance.now() - startConvert;
        irisbdbPlaceholder = toSignedByteJson(isoBuf);
      } else if (fileExtension === 'iso') {
        const isoArrayBuffer = await file.arrayBuffer();
        convertJpegDuration = performance.now() - startConvert;
        irisbdbPlaceholder = toSignedByteJson(isoArrayBuffer);
      } else {
        throw new Error('Unsupported file type.');
      }

      const templateResponse = await fetch('/iris_extract_inner_request-template.json');
      if (!templateResponse.ok) throw new Error(`Template fetch failed with status: ${templateResponse.status}`);

      const templateText = await templateResponse.text();
      const updatedTemplate = templateText
        .replace(/<iris_subtype>/g, irisType)
        .replace(/irisbdb/g, irisbdbPlaceholder);

      const versionRequestTemplateResponse = await fetch('/extract_request-template.json');
      if (!versionRequestTemplateResponse.ok) {
        throw new Error(`Version request template fetch failed with status: ${versionRequestTemplateResponse.status}`);
      }

      const versionRequestTemplateText = await versionRequestTemplateResponse.text();
      const base64EncodedExtract = arrayBufferToBase64(new TextEncoder().encode(updatedTemplate));
      const finalRequestBodyExtract = versionRequestTemplateText.replace('encoded_request', base64EncodedExtract);

      const startExtract = performance.now();
      const isRawInput = fileExtension !== 'iso';
      const extractUrl = biosdkOperationUrl('extract-template', isRawInput);
      logBiosdkUiRoute('IrisExtractor', isRawInput ? 'rawRoute' : 'isoRoute', extractUrl, {
        operation: 'extract-template',
      });
      const extractResponse = await axios.post(extractUrl, finalRequestBodyExtract, {
        headers: { 'Content-Type': 'application/json' },
      });
      const extractDuration = performance.now() - startExtract;

      const segment = extractResponse.data?.response?.response?.segments?.[0];
      const bdb = segment?.bdb;
      const others = segment?.others || {};
      const extractionMs = Number(others?.extractionDurationMilliSeconds) || extractDuration;

      if (!bdb || !bdb.length) {
        const errMsg =
          extractResponse.data?.response?.statusMessage ||
          extractResponse.data?.errors?.[0]?.message ||
          'Extraction returned an empty template (check BioSDK logs and OpenIris service).';
        throw new Error(errMsg);
      }

      const u8 = new Uint8Array(bdb);
      let templateSummary = { formatLabel: 'OpenIris template (JSON in BDB)', parsed: false };
      try {
        const text = new TextDecoder().decode(u8);
        const j = JSON.parse(text);
        templateSummary = {
          formatLabel: 'OpenIris (OPENIRIS_V1)',
          parsed: true,
          modelName: j.model_name,
          schemaVersion: j.schema_version,
        };
      } catch {
        /* binary or non-JSON BDB */
      }

      const tplUrl = URL.createObjectURL(new Blob([u8], { type: 'application/octet-stream' }));
      setTemplateBlobUrl(tplUrl);

      setExtractResult({
        status: 'success',
        message: 'Iris template extracted via BioSDK (OpenIris)',
        details: {
          irisType,
          outputSizeBytes: u8.length,
          templateSummary,
        },
      });

      setTiming({
        convertInput: timing.convertInput,
        convertJpeg: convertJpegDuration,
        extractionDurationMilliSeconds: extractionMs,
      });

      setShowAdvanced(true);
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'An error occurred during the extraction process';
      setError(errorMessage);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDownload = () => {
    if (templateBlobUrl) {
      const link = document.createElement('a');
      link.href = templateBlobUrl;
      link.download = 'extracted-image.iso';
      link.click();
    }
  };

  const handleReset = () => {
    setFile(null);
    setIrisType('Left');
    setTemplateBlobUrl(null);
    setInJpegImageUrl(null);
    setJp2IsoImageUrl(null);
    setExtractResult(null);
    setError(null);
    setSubmitted(false);
    setShowAdvanced(false);
  };

  return (
    <div className="biochq-page">
      <div className="biochq-container">
        <Card className="biochq-card iris">
          <CardHeader className="text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Eye className="h-8 w-8 text-iris" />
              <CardTitle className="text-3xl font-bold text-gray-800">Iris Extractor</CardTitle>
            </div>
            <CardDescription className="text-gray-600">
              Extract biometric templates from Iris images using ISO/IEC 19794-4 standard
            </CardDescription>
          </CardHeader>

          <CardContent>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="irisType" className="text-gray-700 font-medium">
                    Iris Type
                  </Label>
                  <Select
                    id="irisType"
                    value={irisType}
                    onChange={(e) => setIrisType(e.target.value)}
                    className="w-full"
                  >
                    <option value="Left">Left Eye</option>
                    <option value="Right">Right Eye</option>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="file" className="text-gray-700 font-medium">
                    Upload Iris Image
                  </Label>
                  <div className="upload-zone">
                    <input
                      type="file"
                      id="file"
                      accept=".png,.jpg,.jpeg,.bmp,.jp2,.iso"
                      onChange={handleFileChange}
                      className="hidden"
                    />
                    <label htmlFor="file" className="cursor-pointer">
                      <Upload className="h-10 w-10 mx-auto text-gray-400 mb-2" />
                      <p className="text-sm text-gray-600">Click to upload or drag and drop</p>
                      <p className="text-xs text-gray-400 mt-1">PNG, JPG, BMP, JP2, or ISO</p>
                    </label>
                  </div>
                </div>

                {file && injpegImageUrl && (
                  <div className="space-y-2">
                    <Label className="text-gray-700 font-medium">Preview</Label>
                    <div className="preview-container">
                      <img src={injpegImageUrl} alt="Uploaded iris" className="preview-image" />
                      <Badge variant="iris" className="absolute top-2 right-2">
                        {irisType} Eye
                      </Badge>
                    </div>
                    <p className="text-xs text-gray-500 text-center">{file.name}</p>
                  </div>
                )}

                <div className="flex gap-3">
                  <Button onClick={handleSubmit} disabled={!file || isProcessing} variant="iris" className="flex-1">
                    {isProcessing ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <Eye className="mr-2 h-4 w-4" />
                        Extract Template
                      </>
                    )}
                  </Button>
                  <Button onClick={handleReset} variant="outline" disabled={isProcessing}>
                    Reset
                  </Button>
                </div>
              </div>

              <div className="space-y-6">
                {error && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>Error</AlertTitle>
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                {extractResult && extractResult.status === 'success' && (
                  <>
                    <Alert variant="success">
                      <CheckCircle2 className="h-4 w-4" />
                      <AlertTitle>Extraction Complete</AlertTitle>
                      <AlertDescription>{extractResult.message}</AlertDescription>
                    </Alert>

                    <Card className="bg-gray-50">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-lg">Extraction Details</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Iris Type:</span>
                          <Badge variant="iris">{extractResult.details.irisType}</Badge>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Template size:</span>
                          <span className="font-medium">{extractResult.details.outputSizeBytes} bytes</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Format:</span>
                          <span className="font-medium text-xs text-right max-w-[60%]">
                            {extractResult.details.templateSummary.formatLabel}
                          </span>
                        </div>
                        {extractResult.details.templateSummary.parsed && (
                          <>
                            {extractResult.details.templateSummary.modelName != null && (
                              <div className="flex justify-between">
                                <span className="text-gray-600">Model:</span>
                                <span className="font-medium text-xs">{extractResult.details.templateSummary.modelName}</span>
                              </div>
                            )}
                            {extractResult.details.templateSummary.schemaVersion != null && (
                              <div className="flex justify-between">
                                <span className="text-gray-600">Schema:</span>
                                <span className="font-medium text-xs">{extractResult.details.templateSummary.schemaVersion}</span>
                              </div>
                            )}
                          </>
                        )}
                      </CardContent>
                    </Card>

                    {showAdvanced && (
                      <Card className="timing-card">
                        <CardHeader className="pb-2">
                          <CardTitle className="text-lg flex items-center gap-2">
                            <Clock className="h-4 w-4" />
                            Timing Details
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Display conversion:</span>
                            <span className="font-medium">{timing.convertInput.toFixed(2)} ms</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Image → ISO (submit):</span>
                            <span className="font-medium">{timing.convertJpeg.toFixed(2)} ms</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Extraction:</span>
                            <span className="font-medium text-primary">
                              {timing.extractionDurationMilliSeconds.toFixed(2)} ms
                            </span>
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    <Button onClick={handleDownload} variant="outline" className="w-full" disabled={!templateBlobUrl}>
                      <Download className="mr-2 h-4 w-4" />
                      Download ISO Template
                    </Button>
                  </>
                )}

                {!extractResult && !error && submitted && (
                  <div className="flex items-center justify-center h-48 text-gray-400">
                    <Loader2 className="h-8 w-8 animate-spin" />
                  </div>
                )}

                {!extractResult && !error && !submitted && (
                  <div className="empty-state">
                    <Eye className="h-12 w-12 mb-2 opacity-50" />
                    <p className="text-sm">Upload an iris image and click Extract</p>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default IrisExtractor;
